"use client";

/**
 * The Talent Pass on the contest page - `EX-TAL01`, `docs/29` §6.26, ADR-0185.
 *
 * Two pieces on the door's dark card:
 * - {@link TalentPassDoorTick}, the tick at registration: "this round's
 *   sponsors may see my profile", naming the companies, with what they see.
 *   The door gives it after the registration succeeds.
 * - {@link TalentPassCard}, for a contestant of the round: the state, the
 *   switch to give or take it back at any time, and a sponsor added after
 *   they ticked, which a tick again includes.
 *
 * Neither mounts its hook unless the page carries the round's brand, so a
 * contest without sponsors reads nothing more than it always did; each draws
 * nothing unless the server says the round offers the pass.
 */
import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { BadgeCheck, Check, ChevronDown, Loader2 } from "lucide-react";

import { Button, SWITCH_KNOB } from "@/components/ui/button";
import { formatDate } from "@/lib/format-datetime";
import { cn } from "@/lib/utils";
import { useMyTalentPass, useSetMyTalentPass } from "../hooks";
import type { PublicContestDetail } from "../types";

/** Whether the page may ask at all: the round's brand is on it (the stamp, and something set). */
export function talentPassMayShow(contest: Pick<PublicContestDetail, "brand" | "viewer">): boolean {
  return Boolean(contest.brand) && contest.viewer.isAuthenticated;
}

function namesOf(companies: readonly { name: string }[]): string {
  return companies.map((company) => company.name).join(", ");
}

/** What a sponsor sees, and what it never sees, in three lines. */
function WhatTheySee({ id }: { id: string }) {
  const t = useTranslations("contest.talentPass");
  return (
    <ul id={id} className="mt-2.5 space-y-1.5 text-[12.5px] leading-relaxed text-neutral-300" data-testid="talent-what">
      {(["whatPlace", "whatProfile"] as const).map((key) => (
        <li key={key} className="flex items-start gap-2">
          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-300" aria-hidden />
          {t(key)}
        </li>
      ))}
      <li className="flex items-start gap-2">
        <span aria-hidden className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[11px] font-bold text-neutral-400">
          ×
        </span>
        {t("whatNever")}
      </li>
      <li className="pt-0.5 text-neutral-400">{t("whatChange")}</li>
    </ul>
  );
}

function WhatTheySeeToggle({ id }: { id: string }) {
  const t = useTranslations("contest.talentPass");
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="linkDark"
        size="mini"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
        className="-ml-2.5 mt-1"
        data-testid="talent-what-toggle"
      >
        {t("whatTitle")}
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-200", open && "rotate-180")} aria-hidden />
      </Button>
      {open ? <WhatTheySee id={id} /> : null}
    </>
  );
}

/**
 * The tick at the door. Controlled by the door, which gives the consent after
 * the registration it belongs to succeeds; nothing is sent from here.
 */
export function TalentPassDoorTick({
  slug,
  checked,
  onChange,
}: {
  slug: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  const t = useTranslations("contest.talentPass");
  const pass = useMyTalentPass(slug);
  if (!pass.data?.offered || pass.data.companies.length === 0) return null;
  return (
    <div className="rounded-[10px] border border-white/10 bg-white/[0.04] px-3.5 py-3" data-testid="talent-door">
      <label className="flex items-start gap-2.5 text-[13px] leading-relaxed text-neutral-200">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/30 bg-transparent text-primary focus:ring-primary"
          data-testid="talent-door-tick"
        />
        <span>{t("doorTick", { companies: namesOf(pass.data.companies) })}</span>
      </label>
      <div className="pl-[26px]">
        <WhatTheySeeToggle id={`talent-door-what-${slug}`} />
      </div>
    </div>
  );
}

/**
 * The card for a contestant of the round: the state and the switch.
 * `autoGive` is the door's tick: the card gives the consent once, as soon as
 * the server says this person may, which is right after the registration.
 */
export function TalentPassCard({ slug, autoGive = false }: { slug: string; autoGive?: boolean }) {
  const t = useTranslations("contest.talentPass");
  const locale = useLocale();
  const pass = useMyTalentPass(slug);
  const set = useSetMyTalentPass(slug);
  const [note, setNote] = useState<"on" | "off" | "failed" | null>(null);
  const gave = useRef(false);

  const data = pass.data;
  const change = async (next: boolean) => {
    setNote(null);
    try {
      await set.mutateAsync(next);
      setNote(next ? "on" : "off");
    } catch {
      setNote("failed");
    }
  };
  useEffect(() => {
    if (!autoGive || gave.current || !data?.canGive || data.consent?.state === "given") return;
    gave.current = true;
    void change(true);
    // `change` is recreated each render; the ref is what keeps this to one give.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGive, data]);

  if (!data || (!data.offered && data.consent?.state !== "given")) return null;

  const on = data.consent?.state === "given";
  const missing = on ? data.companies.filter((company) => !data.consent!.companyIds.includes(company.id)) : [];
  // Withdrawing always works; giving needs the server's yes.
  const disabled = set.isPending || (!on && !data.canGive);
  const why = !on && !data.canGive && data.window.state !== "closed" ? t("cannotGive") : null;
  const covering = namesOf(data.companies.filter((company) => data.consent?.companyIds.includes(company.id)));
  // The sentence under the title: what is true now.
  const status =
    data.window.state === "closed"
      ? on
        ? t("statusClosedOn")
        : t("statusClosed")
      : !on
        ? t("statusOff")
        : data.window.state === "open"
          ? data.window.closesAt
            ? t("statusOnOpen", { companies: covering, date: formatDate(data.window.closesAt, locale) })
            : t("statusOnOpenNoDate", { companies: covering })
          : t("statusOnWaiting", { companies: covering });

  return (
    <section
      aria-labelledby={`talent-title-${slug}`}
      className={cn(
        "rounded-[10px] border px-4 py-3.5 transition-colors duration-200",
        on ? "border-emerald-400/30 bg-emerald-400/[0.07]" : "border-white/10 bg-white/[0.04]",
      )}
      data-testid="talent-card"
      data-state={on ? "on" : "off"}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-200",
            on ? "bg-emerald-400/15 text-emerald-300" : "bg-white/10 text-neutral-300",
          )}
        >
          <BadgeCheck className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <h3 id={`talent-title-${slug}`} className="text-sm font-semibold text-white">
              {t("title")}
            </h3>
            <span className="flex items-center gap-2">
              {set.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" aria-hidden /> : null}
              <Button
                type="button"
                role="switch"
                variant="switch"
                size="switch"
                aria-checked={on}
                aria-label={t("switchLabel")}
                disabled={disabled}
                onClick={() => void change(!on)}
                data-testid="talent-switch"
              >
                <span className={SWITCH_KNOB} />
              </Button>
            </span>
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-neutral-300" data-testid="talent-status">
            {status}
          </p>
          {data.companies.length > 0 ? (
            <ul className="mt-2 flex flex-wrap gap-1.5" aria-label={t("companiesLabel")}>
              {data.companies.map((company) => {
                const covered = on && data.consent!.companyIds.includes(company.id);
                return (
                  <li
                    key={company.id}
                    className={cn(
                      "rounded-full border px-2.5 py-0.5 text-xs font-medium",
                      covered ? "border-emerald-300/40 text-emerald-100" : "border-white/15 text-neutral-300",
                    )}
                  >
                    {company.name}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {missing.length > 0 && data.canGive ? (
            <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[12.5px] text-amber-200" data-testid="talent-missing">
              <span>{t("missing", { companies: namesOf(missing) })}</span>
              <Button type="button" variant="outlineDark" size="mini" disabled={set.isPending} onClick={() => void change(true)}>
                {t("includeAll")}
              </Button>
            </div>
          ) : null}
          {why ? <p className="mt-2 text-[12.5px] text-neutral-400">{why}</p> : null}
          <WhatTheySeeToggle id={`talent-card-what-${slug}`} />
          {note ? (
            <p
              role="status"
              className={cn("mt-1.5 text-[12.5px] font-medium", note === "failed" ? "text-red-200" : "text-emerald-200")}
              data-testid="talent-note"
            >
              {note === "on" ? t("savedOn") : note === "off" ? t("savedOff") : t("saveFailed")}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
